import React, { useState } from "react";
import { FiImage, FiStar, FiEdit3, FiTrash2, FiUpload, FiX, FiCheck, FiGrid, FiTag } from "react-icons/fi";

import SectionHeader from "../components/ui/SectionHeader";
import {
  useCreateGalleryItemMutation,
  useDeleteGalleryItemMutation,
  useGetGalleryItemsQuery,
  useUpdateGalleryItemMutation,
} from "../services/api";
import { getApiErrorMessage } from "../utils/apiError";

const emptyForm = {
  title: "",
  category: "Portfolio",
  description: "",
  image_url: "",
  featured: true,
  sort_order: 0,
};

const MAX_GALLERY_IMAGE_SIZE = 1600;
const GALLERY_IMAGE_QUALITY = 0.82;

function resizeImageFile(file) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      const scale = Math.min(1, MAX_GALLERY_IMAGE_SIZE / Math.max(image.width, image.height));
      const width = Math.round(image.width * scale);
      const height = Math.round(image.height * scale);
      const canvas = document.createElement("canvas");

      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(image, 0, 0, width, height);
      URL.revokeObjectURL(objectUrl);
      resolve(canvas.toDataURL("image/jpeg", GALLERY_IMAGE_QUALITY));
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to read this image. Please choose another file."));
    };

    image.src = objectUrl;
  });
}

const GalleryPage = () => {
  const { data, isLoading } = useGetGalleryItemsQuery();
  const [createGalleryItem, { isLoading: isCreating }] = useCreateGalleryItemMutation();
  const [updateGalleryItem, { isLoading: isUpdating }] = useUpdateGalleryItemMutation();
  const [deleteGalleryItem] = useDeleteGalleryItemMutation();
  const [editingId, setEditingId] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [uploadLabel, setUploadLabel] = useState("No image selected");
  const [form, setForm] = useState(emptyForm);

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      setSubmitError("Please choose a valid image file.");
      return;
    }

    try {
      setUploadLabel("Optimizing image...");
      const imageDataUrl = await resizeImageFile(file);
      setForm((current) => ({ ...current, image_url: imageDataUrl }));
      setUploadLabel(file.name);
      setSubmitError("");
    } catch (error) {
      setUploadLabel("No image selected");
      setSubmitError(error.message || "Unable to process this image.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    try {
      if (editingId) {
        await updateGalleryItem({ id: editingId, ...form }).unwrap();
      } else {
        await createGalleryItem(form).unwrap();
      }
      setEditingId(null);
      setForm(emptyForm);
      setUploadLabel("No image selected");
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, "Unable to save gallery item."));
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm(item);
    setUploadLabel("Using saved image");
    setSubmitError("");
  };

  const handleCancel = () => {
    setEditingId(null);
    setForm(emptyForm);
    setUploadLabel("No image selected");
    setSubmitError("");
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this gallery item?")) {
      try {
        await deleteGalleryItem(id).unwrap();
      } catch (error) {
        alert("Failed to delete item. Please try again.");
      }
    }
  };

  const galleryItems = data?.data || [];
  const isSaving = isCreating || isUpdating;

  if (isLoading) {
    return <div className="loading-state">Loading gallery...</div>;
  }

  return (
    <div className="dashboard-page">
      <SectionHeader
        eyebrow="Portfolio Gallery"
        title="Manage Portfolio Images"
        description="Add, edit, and organize portfolio images shown on the public website. Featured items appear prominently on the homepage."
      />

      <div className="dashboard-grid dashboard-grid--wide">
        {/* Gallery Items Grid */}
        <section className="panel" style={{ gridColumn: "1 / -1" }}>
          <div style={{ padding: "20px", borderBottom: "1px solid var(--border, #e5e7eb)" }}>
            <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
              <FiGrid /> Gallery Items ({galleryItems.length})
            </h3>
          </div>

          {galleryItems.length === 0 ? (
            <div style={{ padding: "60px 20px", textAlign: "center", color: "var(--text-muted, #888)" }}>
              <FiImage size={48} style={{ opacity: 0.3, marginBottom: "16px" }} />
              <p style={{ fontSize: "1rem", fontWeight: 600, marginBottom: "8px" }}>No gallery items yet</p>
              <p style={{ fontSize: "0.875rem" }}>Add your first portfolio image using the form below</p>
            </div>
          ) : (
            <div className="gallery-admin-grid">
              {galleryItems.map((item) => (
                <div key={item.id} className="gallery-admin-card">
                  <div className="gallery-admin-card__image-wrapper">
                    <img 
                      src={item.image_url} 
                      alt={item.title} 
                      className="gallery-admin-card__image" 
                    />
                    {item.featured && (
                      <div className="gallery-admin-card__badge">
                        <FiStar size={14} /> Featured
                      </div>
                    )}
                  </div>
                  <div className="gallery-admin-card__content">
                    <h4 className="gallery-admin-card__title">{item.title}</h4>
                    <div className="gallery-admin-card__meta">
                      <span className="gallery-admin-card__category">
                        <FiTag size={12} /> {item.category}
                      </span>
                      <span className="gallery-admin-card__sort">Sort: {item.sort_order}</span>
                    </div>
                    {item.description && (
                      <p className="gallery-admin-card__description">{item.description}</p>
                    )}
                    <div className="gallery-admin-card__actions">
                      <button
                        className="btn-icon btn-icon--edit"
                        type="button"
                        onClick={() => handleEdit(item)}
                        title="Edit item"
                      >
                        <FiEdit3 size={16} />
                      </button>
                      <button
                        className="btn-icon btn-icon--delete"
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        title="Delete item"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Add/Edit Form */}
        <form className="form-modern" onSubmit={handleSubmit} style={{ gridColumn: "1 / -1" }}>
          <div className="form-section">
            <div className="form-section__header">
              <h3>{editingId ? "Edit Gallery Item" : "Add New Gallery Item"}</h3>
              <p>Upload and configure portfolio images for your website</p>
            </div>

            {submitError && (
              <div className="form-alert form-alert--danger">
                <FiX />
                <span>{submitError}</span>
              </div>
            )}

            <div className="form-grid">
              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label className="form-label form-label--required">
                  Portfolio Title
                </label>
                <div className="form-input-group">
                  <div className="form-input-group__icon">
                    <FiImage />
                  </div>
                  <input
                    className="form-input"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="e.g., Business Card Design"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label form-label--required">
                  Category
                </label>
                <div className="form-input-group">
                  <div className="form-input-group__icon">
                    <FiTag />
                  </div>
                  <input
                    className="form-input"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="e.g., Branding, Printing, etc."
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Sort Order
                </label>
                <input
                  className="form-input"
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
                  placeholder="0"
                />
                <small className="form-helper">Lower numbers appear first</small>
              </div>

              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label className="form-label form-label--required">
                  Image Upload
                </label>
                <div className="form-upload-area">
                  <FiUpload size={24} />
                  <p>
                    <strong>Click to upload</strong> or drag and drop
                  </p>
                  <small>PNG, JPG, GIF up to 10MB</small>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{
                      position: "absolute",
                      inset: 0,
                      opacity: 0,
                      cursor: "pointer",
                    }}
                  />
                </div>
                {uploadLabel !== "No image selected" && (
                  <div className="form-upload-status">
                    <FiCheck /> {uploadLabel}
                  </div>
                )}
              </div>

              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label className="form-label">
                  Or Paste Image URL
                </label>
                <input
                  className="form-input"
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              {form.image_url && (
                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                  <label className="form-label">Image Preview</label>
                  <div className="gallery-form-preview-wrapper">
                    <img
                      src={form.image_url}
                      alt="Preview"
                      className="gallery-form-preview"
                    />
                  </div>
                </div>
              )}

              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label className="form-label">
                  Description
                </label>
                <textarea
                  className="form-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description of the project..."
                  rows="4"
                />
              </div>

              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label className="form-checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(form.featured)}
                    onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  />
                  <span>
                    <FiStar size={16} /> Show as featured on homepage
                  </span>
                </label>
                <small className="form-helper">Featured items are displayed prominently</small>
              </div>
            </div>

            <div className="form-actions">
              <button
                className={`btn-primary ${isSaving ? "btn-primary--loading" : ""}`}
                type="submit"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : editingId ? "Update Gallery Item" : "Add Gallery Item"}
              </button>
              {editingId && (
                <button
                  className="btn-secondary"
                  type="button"
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GalleryPage;
